import { useState } from "react";
import { Button, Icon, Loading, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import {
  clientPaymentApi,
  useDeleteClientPaymentMethodMutation,
  useGetClientPaymentMethodsQuery,
  useSetDefaultPaymentMethodMutation,
} from "@/redux/apis/clientPayment/clientPaymentApi";
import { ClientPaymentMethod, PaymentMethodType } from "@/redux/apis/clientPayment/types";
import { useGetClientProfileQuery } from "@/redux/apis/clientProfile/clientProfileApi";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { accountIdSelector } from "@/redux/slices/account/selectors";
import { configSelector } from "@/redux/slices/config/selectors";
import { addToast } from "@/utils";
import { CardFormModal } from "./components/CardFormModal";
import { CardRow } from "./components/CardRow";
import { CardSecurityTooltip } from "./components/CardSecurityTooltip";
import { DeleteCardModal } from "./components/DeleteCardModal";
import { InvoiceBillingCard } from "./components/InvoiceBillingCard";
import { isCreditCard, MAX_CARDS_PER_ACCOUNT } from "./helpers";

type ActiveModal = { type: "add" } | { type: "edit"; card: ClientPaymentMethod } | { type: "delete"; card: ClientPaymentMethod } | null;

export const PaymentMethodsCard = ({ accountId: managedAccountId }: { accountId?: string }) => {
  const dispatch = useAppDispatch();
  const selfAccountId = useAppSelector(accountIdSelector);
  const accountId = managedAccountId ?? selfAccountId;
  // Shared card: operator-managed view uses 3rd-person copy, client self-service keeps 2nd-person.
  const isOperatorView = !!managedAccountId;

  const { data: paymentMethods = [], isLoading, error, refetch } = useGetClientPaymentMethodsQuery(accountId, { skip: !accountId });
  const [deletePaymentMethod, { isLoading: isDeleting }] = useDeleteClientPaymentMethodMutation();
  const [setDefaultPaymentMethod, { isLoading: isSettingDefault }] = useSetDefaultPaymentMethodMutation();

  const [isEditing, setIsEditing] = useState(false);
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);

  const { data: clientProfile } = useGetClientProfileQuery(managedAccountId ? { accountId: managedAccountId } : undefined);
  const operatorName = useAppSelector(configSelector)?.name;

  const cards = paymentMethods.filter(isCreditCard);
  const invoiceMethod = paymentMethods.find((m) => m.type === PaymentMethodType.INVOICE);
  const companyName = clientProfile?.company?.company_name;
  // Gate add-card solely on count — card type is now auto-assigned server-side (DEV-1142).
  // The backend caps active cards at MAX_CARDS_PER_ACCOUNT.
  const canAddCard = cards.length < MAX_CARDS_PER_ACCOUNT;

  const closeModal = () => setActiveModal(null);

  // Edit→delete are both vaul Drawers on mobile; two root drawers transitioning in the same tick
  // cancel each other (the opening one closes immediately). Close the edit drawer first, then open
  // the delete drawer after its close animation (~vaul default) so only one is ever transitioning.
  const requestDelete = (cardToDelete: ClientPaymentMethod) => {
    closeModal();
    setTimeout(() => setActiveModal({ type: "delete", card: cardToDelete }), 350);
  };

  const refreshList = () => dispatch(clientPaymentApi.util.invalidateTags(["ClientPaymentMethods"]));

  const handleSaved = async (newPaymentMethodId: string, replacedCard?: ClientPaymentMethod, isRename?: boolean) => {
    if (isRename) {
      addToast("success", "Credit card updated");
    } else if (replacedCard) {
      // replacement id lets the backend repoint the old card's unpaid jobs so in-use cards can be swapped
      await deletePaymentMethod({ accountId, paymentMethodId: replacedCard.uuid, replacementId: newPaymentMethodId })
        .unwrap()
        .then(() => addToast("success", "Credit card updated"))
        .catch((e) => {
          getErrorMessages(e).forEach((message) => addToast("danger", message));
          addToast("danger", "The new card was added but the old one could not be removed");
        });
    } else {
      addToast("success", "Successfully added new credit card");
    }
    refreshList();
    closeModal();
  };

  const handleDelete = () => {
    if (activeModal?.type !== "delete") return;
    deletePaymentMethod({ accountId, paymentMethodId: activeModal.card.uuid })
      .unwrap()
      .then(() => {
        addToast("success", "Credit card removed");
        closeModal();
      })
      .catch((e) => getErrorMessages(e).forEach((message) => addToast("danger", message)));
  };

  const handleSetDefault = (card: ClientPaymentMethod) => {
    setDefaultPaymentMethod({ accountId, paymentMethodId: card.uuid })
      .unwrap()
      .then(() => addToast("success", "Default payment method updated"))
      .catch((e) => getErrorMessages(e).forEach((message) => addToast("danger", message)));
  };

  const renderAddCardTile = () => (
    <button
      type="button"
      className="flex h-[52px] w-full items-center gap-3.5 rounded-lg border border-dashed border-info bg-info-light px-3.5 hover:bg-info-light/70"
      onClick={() => setActiveModal({ type: "add" })}
    >
      <Icon name="Add" size={24} className="text-info" />
      <Typography variant="action" className="text-info-dark">
        Add credit card
      </Typography>
    </button>
  );

  const renderContent = () => {
    if (isLoading) return <Loading className="relative min-h-[120px] !bg-transparent" />;

    if (error)
      return (
        <div className="flex flex-col items-start gap-2">
          <Typography variant="paragraph" className="text-danger">
            {getErrorMessages(error)[0] ?? "Unable to load your payment methods."}
          </Typography>
          <Button variant="secondary" size="sm" onClick={refetch}>
            Try again
          </Button>
        </div>
      );

    return (
      <div className="flex flex-col gap-4">
        {invoiceMethod && <InvoiceBillingCard companyName={companyName} operatorName={operatorName} isDefault={!!invoiceMethod.default} />}

        {cards.length === 0 && (
          <div className="flex items-center gap-2 rounded-lg border border-warning bg-warning-light py-1 pl-2 pr-4">
            <Icon name="Danger" variant="Bold" size={20} className="shrink-0 text-warning" />
            <div className="flex flex-col">
              <Typography variant="action" className="text-warning-dark">
                No Payment Method Selected
              </Typography>
              <Typography variant="small-regular" className="!font-medium text-warning-dark">
                Please add a credit card to book a trip.
              </Typography>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2">
          <Icon name="Card" size={20} className="text-neutral-black" />
          <Typography variant="action" className="text-neutral-black">
            Credit Card Payments
          </Typography>
        </div>
        <CardSecurityTooltip />

        {cards.length >= 2 && (
          <div className="flex flex-col gap-1 rounded-lg bg-warning-light p-2.5 sm:flex-row sm:items-start sm:gap-2.5">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-warning">
              <Icon name="LampCharge" variant="Bold" size={18} className="text-white" />
            </div>
            <div className="flex flex-col">
              <Typography variant="small" className="text-neutral-dark-gray">
                Good to know before you book
              </Typography>
              <Typography variant="small-regular" className="!font-medium text-neutral-dark-gray">
                {isOperatorView
                  ? "When the client books, double-check the card selected is the right one. Payments made to the wrong card can't be reversed."
                  : "When you book, double-check the card you've selected is the right one. Payments made to the wrong card can't be reversed."}
              </Typography>
            </div>
          </div>
        )}

        {cards.map((card) => (
          <CardRow
            key={card.uuid}
            method={card}
            isEditing={isEditing}
            isSettingDefault={isSettingDefault}
            lockedByInvoice={!!invoiceMethod}
            onEditCard={(c) => setActiveModal({ type: "edit", card: c })}
            onSetDefault={handleSetDefault}
          />
        ))}

        {(isEditing || cards.length === 0) && canAddCard && renderAddCardTile()}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-5 rounded-lg border border-neutral-mid-gray bg-white p-5">
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <Typography variant="title" className="text-neutral-silver-gray">
            Payment Methods
          </Typography>
          <Typography variant="paragraph" className="text-neutral-dark-gray">
            {isOperatorView
              ? "Choose the method to use by default. The client can switch methods for individual trips at checkout."
              : "Choose the method we'll use by default. You can switch methods for individual trips at checkout."}
          </Typography>
        </div>
        {!isLoading && !error && cards.length > 0 && (
          <div className="flex shrink-0 items-center gap-2">
            {isEditing ? (
              <>
                <Button variant="secondary" size="sm" onClick={() => setIsEditing(false)}>
                  Cancel
                </Button>
                <Button variant="primary" size="sm" onClick={() => setIsEditing(false)}>
                  Save
                </Button>
              </>
            ) : (
              <Button variant="secondary" size="sm" endIcon="Edit2" onClick={() => setIsEditing(true)}>
                Edit
              </Button>
            )}
          </div>
        )}
      </div>

      {renderContent()}

      <CardFormModal
        open={activeModal?.type === "add" || activeModal?.type === "edit"}
        mode={activeModal?.type === "edit" ? "edit" : "add"}
        accountId={accountId}
        card={activeModal?.type === "edit" ? activeModal.card : undefined}
        onClose={closeModal}
        onSaved={handleSaved}
        onRequestDelete={requestDelete}
      />

      <DeleteCardModal open={activeModal?.type === "delete"} isDeleting={isDeleting} onClose={closeModal} onConfirm={handleDelete} />
    </div>
  );
};
